/**
 * @file Pure brief logic — zero DOM dependency.
 *
 * During Vue migration:
 *   - assembleEnrichment → useBriefStore().assembleEnrichment()
 *   - calcCompleteness   → useBriefStore().completeness (computed)
 *   - canProceed         → useBriefStore().canProceed (computed)
 */
import type { Brief, Enrichment, FollowUp } from '../types.ts';

// ── ENRICHMENT ASSEMBLY ────────────────────────────────────────────

const CATEGORY_KEY_MAP: Record<string, keyof Omit<Enrichment, 'sources'>> = {
  DIFFERENTIATION: 'differentiation',
  PROOF: 'proof',
  OBJECTION: 'objections',
  TRANSFORMATION: 'transformation',
  PROCESS: 'process',
  LOCAL: 'localContext',
};

/**
 * Assemble enrichment data from follow-up answers.
 * Maps follow-up categories to enrichment keys, using fallback text
 * when user skipped a question.
 */
export const assembleEnrichment = (followUps: FollowUp[]): Enrichment => {
  const e: Enrichment = { sources: {} };

  for (const fu of followUps) {
    const key = CATEGORY_KEY_MAP[fu.category];
    if (!key) {
      continue;
    }

    if (fu.answer?.trim() && !fu.skipped) {
      (e as Record<string, unknown>)[key] = fu.answer.trim();
      e.sources![key] = 'user';
    } else {
      (e as Record<string, unknown>)[key] = fu.fallback;
      e.sources![key] = 'fallback';
    }
  }

  return e;
};

// ── BRIEF COMPLETENESS ─────────────────────────────────────────────

const ENRICHMENT_KEYS = [
  'differentiation',
  'proof',
  'objections',
  'transformation',
  'process',
  'localContext',
] as const;

/**
 * Calculate brief completeness as a percentage (0–100).
 */
export const calcCompleteness = (brief: Brief, enrichment?: Enrichment): number => {
  let total = 10;
  let filled = 0;

  if (brief.businessDesc?.trim()) {
    filled += 2;
  }
  if (brief.customerProblem?.trim()) {
    filled += 2;
  }
  if (brief.desiredAction) {
    filled += 1;
  }
  if (brief.vibes?.length) {
    filled += 1;
  }

  const e = enrichment || {};
  for (const k of ENRICHMENT_KEYS) {
    if (!(e as Record<string, unknown>)[k]) {
      continue;
    }
    total += 1;
    filled += e.sources?.[k] === 'user' ? 1 : 0.3;
  }

  return Math.min(100, Math.round((filled / total) * 100));
};

// ── STEP VALIDATION ────────────────────────────────────────────────

/**
 * Check if the user can proceed from the current brief step.
 */
export const canProceed = (step: number, brief: Brief): boolean => {
  if (step === 0) {
    return !!brief.businessDesc?.trim();
  }
  if (step === 1) {
    return !!brief.customerProblem?.trim();
  }
  if (step === 2) {
    return !!brief.desiredAction;
  }
  if (step === 3) {
    return brief.vibes.length > 0;
  }
  return true;
};
